import {
  Grid,
  Typography,
  Stack,
  Button,
  IconButton,
  Box,
  FormControl,
  MenuItem,
  Select,
  TextField,
  InputLabel,
  SelectChangeEvent,
  DialogActions,
  DialogTitle,
  DialogContent,
  DialogContentText
} from '@mui/material';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import '../StudyLogs.scss';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { archLevel } from 'src/data/config';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { useGetAllPatientDetailsMutation } from 'src/services/patientDetails';
import moment from 'moment';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import printPdfWithViewer from 'src/components/common/PrintPdfWithViewer';
import { showLoader, hideLoader } from 'src/store/Loader';
import { useUser } from 'src/components/hooks/useUser';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { useNoteToFile } from './useNoteToFile';
import { setMessage } from 'src/store/Message';
import { UI_TIME_ZONE } from 'src/constants/formats';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { LoadingButton } from '@mui/lab';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import ESignConsentReAuth from 'src/components/common/ESignConsentReAuth';
import { useLazySetESignQuery } from 'src/services/ESignAPI';
import { useFieldTranslation } from 'src/components/fields/useFieldTranslation';
import { useValidUserForReAuth } from 'src/components/hooks/useValidUserForReAuth';
import { useLazyGetSponsorLogoQuery } from 'src/services/workarea';

const NotetofilePage: React.FunctionComponent<any> = ({
  NoteToFileData,
  selectedRow,
  setOpenNoteTofile,
  setNTFGrid,
  siteStudyPersonnelsData,
  setselectedRow,
  ntfData,
  setNtfData,
  getFileData
}) => {
  const { isValidUserForReAuth } = useValidUserForReAuth();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const email = useAppSelector(state => state.user.user?.email);
  const { selectedAppSection } = useSelectedAppInfo();
  const { applicationModuleSection, unSavedWarning } = useFieldTranslation();
  const [openSignConsent, setOpenSignConsent] = useState(false);
  const { checkPermission } = usePermissionWrapper();
  const [disablePublish, setDisablePublish] = useState<boolean>(false);
  const [disableButtons, setDisableButtons] = useState<boolean>(false);

  const publishEtmfPermission = checkPermission('publish_etmf_note_to_file');
  const publishIsfPermission = checkPermission('publish_isf_note_to_file');
  const { user: userFullName } = useUser();

  const [subjectId, setSubjectId] = useState<any>();
  const [role, setRole] = useState('');
  const dispatch = useAppDispatch();
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [openVersionPopup, setOpenVersionPopup] = useState(false);
  const {
    updateNoteToFile,
    updateNoteToFileError,
    updateNoteToFileIsError,
    updateNoteToFileIsSuccess,
    noteToFileSignError,
    noteToFileSignIsError,
    noteToFileSignIsSuccess,
    createNewNoteToFileVersionError,
    createNewNoteToFileVersionIsError,
    createNewNoteToFileVersionIsSuccess,
    createNewNoteToFileVersion,
    noteToFilePublishToEtmfError,
    noteToFilePublishToEtmfIsError,
    noteToFilePublishToEtmfIsSuccess,
    noteToFilePublishToETMF,
    noteToFilePublishToIsfError,
    noteToFilePublishToIsfIsError,
    noteToFilePublishToIsfIsSuccess,
    noteToFilePublishToISF
  } = useNoteToFile();

  const [setESignature, { isLoading: eSignatureIsLoading }] = useLazySetESignQuery();

  const { userPreferedDateFormat, getCurrentDateInPreferedFormat, getPreferredDateTimeInLocal } =
    usePreferedDateFormat();

  const [getAllPatientDetailsAPI, { data: allPatientDetails }] = useGetAllPatientDetailsMutation();

  useEffect(() => {
    getAllPatientDetailsAPI({
      search_scope: 'SUBJECT_ID',
      study_id: studyId,
      country: selectedCountry,
      site_id: selectedSite,
      application: process.env.REACT_APP_NAME_TEMP,
      application_section: selectedAppSection,
      application_module_section: 'STUDY LOGS'
    });
    const roleVal = siteStudyPersonnelsData.personnel
      .map((items: { site_per_email: string | undefined; site_per_frole: string | undefined }) => {
        if (items.site_per_email?.toLowerCase() === email?.toLowerCase()) {
          return items.site_per_frole;
        }
      })
      .filter((item: any) => {
        if (item !== undefined) {
          return item;
        }
      });
    setRole(roleVal[0]);
  }, []);

  useEffect(() => {
    if (NoteToFileData && typeof NoteToFileData.ntf_is_published_etmf !== null) {
      // Update disableButtons based on the condition
      setDisableButtons(NoteToFileData.ntf_is_published_etmf);
    }
  }, [NoteToFileData]);

  useEffect(() => {
    const subjectID = allPatientDetails?.selected_data
      ?.map((elem: any) => elem?.patient?.subject_id)
      ?.filter((ele: any) => ele !== null);
    setSubjectId([...new Set(subjectID)]);
  }, [allPatientDetails]);

  useEffect(() => {
    if (noteToFileSignIsSuccess) {
      setDisablePublish(true);
      getFileData();
      dispatch(
        setMessage({
          msgDetails: 'Signature done successfully.',
          severity: 'success'
        })
      );
    }
  }, [noteToFileSignIsSuccess]);

  useEffect(() => {
    if (createNewNoteToFileVersionIsSuccess) {
      dispatch(
        setMessage({
          msgDetails: 'New note to file version ready to modify.',
          severity: 'success'
        })
      );
      setOpenVersionPopup(false);
      getFileData();
    }
  }, [createNewNoteToFileVersionIsSuccess]);

  useEffect(() => {
    if (updateNoteToFileIsSuccess) {
      setNtfData({ ...ntfData, ntf_date: new Date().toLocaleDateString('en-CA') });
      getFileData();
      dispatch(
        setMessage({
          msgDetails: 'Data updated successfully.',
          severity: 'success'
        })
      );
      setIsChanged(false);
      setConfirmExitValue(dispatch, false);
    }
  }, [updateNoteToFileIsSuccess]);

  useEffect(() => {
    if (noteToFilePublishToEtmfIsSuccess) {
      setDisableButtons(true);
      setDisablePublish(false);
      getFileData();
      dispatch(
        setMessage({
          msgDetails: 'Published to eTMF Successfully',
          severity: 'success'
        })
      );
    }
  }, [noteToFilePublishToEtmfIsSuccess]);

  useEffect(() => {
    if (noteToFilePublishToIsfIsSuccess) {
      setDisableButtons(true);
      setDisablePublish(false);
      getFileData();
      dispatch(
        setMessage({
          msgDetails: 'Published to eISF Successfully',
          severity: 'success'
        })
      );
    }
  }, [noteToFilePublishToIsfIsSuccess]);

  useEffect(() => {
    setNtfData(selectedRow);
  }, [selectedRow]);

  useEffect(() => {
    if (
      updateNoteToFileIsError ||
      noteToFileSignIsError ||
      createNewNoteToFileVersionIsError ||
      noteToFilePublishToEtmfIsError ||
      noteToFilePublishToIsfIsError
    ) {
      const errorData: any =
        updateNoteToFileError ||
        noteToFileSignError ||
        createNewNoteToFileVersionError ||
        noteToFilePublishToEtmfError ||
        noteToFilePublishToIsfError;
      if (
        errorData?.status == 500 ||
        errorData?.status == 502 ||
        errorData?.status == 503 ||
        errorData?.status == 'FETCH_ERROR'
      ) {
        dispatch(
          setMessage({
            msgDetails: `${errorData?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (errorData?.status == 403) {
        dispatch(
          setMessage({
            msgDetails: `${errorData?.status} : Forbbiden Access`,
            severity: 'error'
          })
        );
      } else if (errorData?.status == 404) {
        dispatch(
          setMessage({
            msgDetails: errorData?.data?.context?.message,
            severity: 'error'
          })
        );
      }
    }
  }, [
    updateNoteToFileIsError,
    noteToFileSignIsError,
    createNewNoteToFileVersionIsError,
    noteToFilePublishToEtmfIsError,
    noteToFilePublishToIsfIsError
  ]);

  const handleChange = (event: SelectChangeEvent) => {
    setNtfData({ ...ntfData, ntf_subject_id: event.target.value });
    setConfirmExitValue(dispatch, true);
    setIsChanged(true);
  };

  const handelBack = () => {
    if (isChanged) {
      setOpenConfirmExit(true);
    } else {
      setNtfData(null);
      setselectedRow(null);
      setNTFGrid(true);
      setOpenNoteTofile(false);
    }
  };

  const handleSave = async () => {
    const data = {
      id: selectedRow?.id,
      ntf_date: new Date().toLocaleDateString('en-CA'),
      ntf_stdy_protocol_name: siteStudyPersonnelsData?.study?.protocol_study_identifier,
      ntf_stdy_protocol_number: siteStudyPersonnelsData?.study?.study_id,
      ntf_subject_id: ntfData.ntf_subject_id,
      ntf_description: ntfData.ntf_description,
      ntf_issue: ntfData.ntf_issue,
      updated_by: userFullName
    };
    await updateNoteToFile({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel == 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
      ntf_level: archLevel.toUpperCase(),
      application_section: selectedAppSection,
      data
    });
  };

  const handelIssue = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNtfData({ ...ntfData, ntf_issue: e.target.value });
    setConfirmExitValue(dispatch, true);
    setIsChanged(true);
  };

  const handelDescription = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNtfData({ ...ntfData, ntf_description: e.target.value });
    setConfirmExitValue(dispatch, true);
    setIsChanged(true);
  };

  const handleSign = async () => {
    if (isChanged) {
      dispatch(
        setMessage({
          msgDetails: 'Note to file changes are not yet saved',
          severity: 'info'
        })
      );
    } else {
      setOpenSignConsent(true);
    }
  };

  const printNoteToFile = async () => {
    try {
      dispatch(showLoader());
      const options = {
        note_to_file_id: NoteToFileData.id,
        template_name: 'Note_To_File',
        study_id: studyId,
        site_id: selectedSite,
        pathVariables: {
          time_zone_id: UI_TIME_ZONE,
          country: selectedCountry,
          appName: process.env.REACT_APP_NAME
        }
      };

      const pdfFileName = archLevel === 'sponsor' ? 'notote_to_file_etmf_pdf_file' : 'notote_to_file_isf_pdf_file';

      printPdfWithViewer(pdfFileName, options, 'application/pdf');
      setTimeout(() => {
        dispatch(hideLoader());
      }, 1500);
    } catch (error: any) {
      dispatch(hideLoader());
      dispatch(setMessage({ msgDetails: 'Error while fetching pdf', severity: 'error' }));
    }
  };
  const publishToEtmf = async () => {
    setDisablePublish(false);
    const dataObject = {
      note_to_file_id: NoteToFileData.id,
      time_zone_id: UI_TIME_ZONE,
      update: NoteToFileData.ntf_publish_version_etmf >= 1 ? true : false,
      ntf_is_tmf_published: true,
      ntf_publish_version_etmf:
        NoteToFileData.ntf_publish_version_etmf >= 1 ? NoteToFileData.ntf_publish_version_etmf + 1 : '1.0',
      updated_by: userFullName
    };

    await noteToFilePublishToETMF({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME,
      application_section: selectedAppSection,
      application_module_section: 'STUDY LOGS',
      data: dataObject
    });
  };
  const publishToISF = async () => {
    setDisablePublish(true);
    const dataObject = {
      note_to_file_id: NoteToFileData.id,
      time_zone_id: UI_TIME_ZONE,
      update: NoteToFileData.ntf_publish_version_isf >= 1 ? true : false,
      ntf_is_isf_published: true,
      ntf_publish_version_isf:
        NoteToFileData.ntf_publish_version_isf >= 1 ? NoteToFileData.ntf_publish_version_isf + 1 : '1.0',
      updated_by: userFullName
    };

    await noteToFilePublishToISF({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: process.env.REACT_APP_NAME_TEMP,
      application_section: selectedAppSection,
      application_module_section: 'STUDY LOGS',
      data: dataObject
    });
  };
  const publishData = async () => {
    if (archLevel == 'site') {
      publishToISF();
    } else {
      publishToEtmf();
    }
  };

  const handleCreateNewVersion = () => {
    setOpenVersionPopup(true);
  };

  const handleCreateNewNTFversion = () => {
    createNewNoteToFileVersion({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: archLevel == 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
      note_to_file_id: NoteToFileData?.id,
      application_section: selectedAppSection,
      ntf_level: archLevel.toUpperCase()
    });
  };

  const handleRowPISign = async (data: any = {}) => {
    if (data.success) {
      if (!isValidUserForReAuth(data.records.user_name)) {
        dispatch(setMessage({ msgDetails: `Please e-sign with the logged in username`, severity: 'error' }));
      } else {
        setOpenSignConsent(false);
        dispatch(showLoader());
        const dataObj = {
          username: data.records.user_name,
          password: data.records.password,
          module_name: 'Note To File',
          name_of_signature: 'ntf_reported_by',
          name_of_date_field: 'ntf_signed_on',
          id: NoteToFileData.id,
          columns_to_change: {
            reason: data.records.sign_reason,
            ntf_role: role,
            updated_by: userFullName
          },
          columns_to_filter: {
            is_submit: ntfData?.is_submit
          }
        };

        const signatureResponse: any = await setESignature({
          site_id: selectedSite,
          study_id: studyId,
          country: selectedCountry,
          application: archLevel == 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP,
          application_section: selectedAppSection,
          application_module_section: applicationModuleSection.noteToFile,
          data: dataObj
        });
        if (signatureResponse.isSuccess) {
          setDisablePublish(true);
          getFileData();
          dispatch(
            setMessage({
              msgDetails: 'Signature done successfully.',
              severity: 'success'
            })
          );
        } else if (signatureResponse.isError) {
          dispatch(setMessage({ msgDetails: `Authorization for ${data.records.user_name} failed`, severity: 'error' }));
        }
        dispatch(hideLoader());
      }
    }
  };

  const [
    getSponsorLogo,
    {
      isLoading: getSponsorLogoIsLoading,
      isError: getSponsorLogoIsError,
      isSuccess: getSponsorLogoSuccess,
      error: getSponsorLogoErrorData,
      data: getSponsorLogoData
    }
  ] = useLazyGetSponsorLogoQuery();

  const [imgaeData, setImageData] = useState<any>(null);

  const imgTypeObj: any = {
    svg: 'data:image/svg+xml;base64',
    png: 'data:image/png;base64',
    jpg: 'data:image/jpg;base64'
  };

  useEffect(() => {
    if (!getSponsorLogoIsLoading && getSponsorLogoData && getSponsorLogoSuccess) {
      setImageData(getSponsorLogoData);
    } else if (!getSponsorLogoIsLoading && getSponsorLogoIsError) {
      setImageData(null);
    }
  }, [
    getSponsorLogoIsLoading,
    getSponsorLogoIsError,
    getSponsorLogoSuccess,
    getSponsorLogoErrorData,
    getSponsorLogoData
  ]);

  useEffect(() => {
    getSponsorLogo({});
  }, []);

  return (
    <>
      <Box className="mainBackBtnWrap">
        <Button onClick={() => handelBack()} startIcon={<ArrowBackIcon fontSize="small" />}>
          Back
        </Button>
      </Box>
      <Grid container spacing={1} sx={{ mb: 1.5, alignItems: 'center' }}>
        <Grid item>
          <Typography variant="h5" component="h5" sx={{ margin: 0, fontSize: '20px', fontWeight: 500 }}>
            {NoteToFileData?.ntf_name}
          </Typography>
        </Grid>
        <Grid item xs sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }}>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="contained"
              sx={{ alignSelf: 'center', textTransform: 'none' }}
              disabled={
                archLevel === 'sponsor'
                  ? NoteToFileData &&
                    NoteToFileData.ntf_reported_by !== null &&
                    NoteToFileData &&
                    NoteToFileData.ntf_is_published_etmf !== true
                    ? !publishEtmfPermission
                    : !disablePublish
                  : NoteToFileData &&
                    NoteToFileData.ntf_reported_by !== null &&
                    NoteToFileData &&
                    NoteToFileData.ntf_is_published_isf !== true
                  ? !publishIsfPermission
                  : !disablePublish
              }
              onClick={publishData}
            >
              {archLevel === 'site' ? 'PUBLISH TO eISF' : 'PUBLISH TO eTMF'}
            </Button>
            <Button
              sx={{ alignSelf: 'center' }}
              disabled={
                ntfData?.ntf_date
                  ? archLevel === 'site'
                    ? !checkPermission('create_new_version_note_to_file_site') || ntfData?.ntf_is_published_isf !== true
                    : !checkPermission('create_new_version_note_to_file_sponsor') ||
                      ntfData?.ntf_is_published_etmf !== true
                  : true
              }
              onClick={handleCreateNewVersion}
            >
              Create new version
            </Button>
            <Button
              sx={{ alignSelf: 'center' }}
              onClick={handleSave}
              disabled={
                (archLevel === 'site'
                  ? !checkPermission('update_site_note_to_file_studylogs')
                  : !checkPermission('update_sponsor_note_to_file_studylogs')) || !isChanged
              }
            >
              Save
            </Button>
            <IconButton
              aria-label="Print"
              color="primary"
              sx={{ alignSelf: 'center' }}
              onClick={printNoteToFile}
              disabled={!disableButtons}
            >
              <LocalPrintshopIcon />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>

      <Box className="letterPage">
        <Grid container spacing={3} sx={{ mt: 1.5 }}>
          <Grid item md={12}>
            {imgaeData?.file_id ? (
              <img
                src={`${imgTypeObj[imgaeData?.file_type?.replace('.', '')?.toLowerCase()]},${imgaeData?.file_data}`}
                alt="BieGene"
              />
            ) : null}
          </Grid>

          <Grid item md={12}>
            <Grid container spacing={2} sx={{ variant: 'body1' }}>
              <Grid item md={4} color="text.secondary">
                <strong>Date: </strong>
                <span className="results">
                  {ntfData?.ntf_date
                    ? moment(ntfData?.ntf_date).format(`${userPreferedDateFormat.toUpperCase()}`)
                    : getCurrentDateInPreferedFormat()}
                </span>
              </Grid>
              <Grid item md={4} color="text.secondary">
                <strong>PI Name: </strong>
                <span className="results">
                  {siteStudyPersonnelsData?.site?.pi_first_name} {siteStudyPersonnelsData?.site?.pi_last_name}
                </span>
              </Grid>
              <Grid item md={4} color="text.secondary">
                <strong>Site Number: </strong>
                <span className="results">{siteStudyPersonnelsData?.site?.site_id}</span>
              </Grid>
              <Grid item md={4} color="text.secondary">
                <strong>Template ID: </strong>
                <span className="results"> {ntfData?.ntf_templated_id}</span>
              </Grid>
              <Grid item md={4} color="text.secondary">
                <strong>Effective Date: </strong>
                <span className="results">{ntfData?.ntf_effective_date}</span>
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={12}>
            <Typography gutterBottom variant="h6" sx={{ mb: 3 }}>
              Study Protocol
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={6} color="text.secondary">
                <strong>Study Protocol Name: </strong>
                <span className="results">{siteStudyPersonnelsData?.study?.protocol_study_identifier}</span>
              </Grid>
              <Grid item md={6} color="text.secondary">
                <strong>Study Protocol Number: </strong>
                <span className="results">{siteStudyPersonnelsData?.study?.study_id}</span>
              </Grid>
              <Grid item md={12}>
                <TextField
                  id=""
                  label="Description"
                  multiline
                  minRows={4}
                  maxRows={10}
                  onChange={e => handelDescription(e)}
                  value={ntfData && ntfData?.ntf_description}
                  disabled={ntfData?.ntf_signed_on}
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={12}>
            <Typography gutterBottom variant="h6">
              Subject ID (if this note to file pertains to a participant)
            </Typography>

            <FormControl variant="standard" sx={{ m: 1, minWidth: 120, maxWidth: 285 }}>
              <InputLabel id="demo-simple-select-standard-label" shrink={ntfData && ntfData?.ntf_subject_id}>
                Subject ID
              </InputLabel>
              <Select
                labelId="demo-simple-select-standard-label"
                id="demo-simple-select-standard"
                value={ntfData && ntfData?.ntf_subject_id}
                onChange={handleChange}
                label="Subject ID"
                disabled={ntfData?.ntf_signed_on}
              >
                {subjectId?.map((item: any, index: number) => {
                  return (
                    <MenuItem value={item} key={index}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid item md={12}>
            <Typography gutterBottom variant="h6">
              Note Issue
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={12}>
                <TextField
                  id=""
                  label="Note/Issue"
                  multiline
                  minRows={4}
                  maxRows={10}
                  onChange={e => handelIssue(e)}
                  value={ntfData?.ntf_issue}
                  disabled={ntfData && ntfData?.ntf_signed_on}
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item md={12}>
            <Typography gutterBottom variant="h6" sx={{ mb: 2 }}>
              Date and Signature
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={6} color="text.secondary">
                <strong>Reported By: </strong>
              </Grid>
              <Grid item md={6} color="text.secondary">
                <strong>Role in Study: </strong>
                <span className="results">{NoteToFileData?.ntf_role}</span>
              </Grid>
              <Grid item md={12}>
                {ntfData?.ntf_reported_by ? (
                  <span className="signedInfo">
                    <strong>{`${NoteToFileData?.ntf_reported_by} `}</strong> on{' '}
                    <span className="results" color="text.secondary">
                      {getPreferredDateTimeInLocal(ntfData?.ntf_signed_on)}
                    </span>{' '}
                    <br />
                    <span className="signReasonrow">
                      <span color="text.secondary">Reason:</span> &ldquo;{ntfData?.reason}&rdquo;
                    </span>
                  </span>
                ) : (
                  <LoadingButton
                    variant="contained"
                    size="large"
                    sx={{ my: 2 }}
                    onClick={handleSign}
                    disabled={
                      ntfData?.is_submit
                        ? archLevel === 'site'
                          ? !checkPermission('sign_site_note_to_file')
                          : !checkPermission('sign_sponsor_note_to_file')
                        : true
                    }
                    loading={eSignatureIsLoading}
                  >
                    Sign
                  </LoadingButton>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <PopupComponent
        open={openConfirmExit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="confirm-modal"
        child
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Exit'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{unSavedWarning}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmExit(false);
            }}
            className="grey-btn"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setIsChanged(false);
              setOpenConfirmExit(false);
              setNTFGrid(true);
              setOpenNoteTofile(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </PopupComponent>
      <PopupComponent aria-labelledby="customized-dialog-title" open={openVersionPopup} maxWidth="lg">
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure do you want to create new version?</Typography>
        </DialogContent>
        <DialogActions>
          <Button className="grey-btn" onClick={() => setOpenVersionPopup(!openVersionPopup)}>
            Cancel
          </Button>
          <Button onClick={handleCreateNewNTFversion}>Create</Button>
        </DialogActions>
      </PopupComponent>
      {openSignConsent && (
        <ESignConsentReAuth
          openSignConsent={openSignConsent}
          setOpenSignConsent={setOpenSignConsent}
          handleConfirmSign={handleRowPISign}
        />
      )}
    </>
  );
};

export default NotetofilePage;
